/**
 * The helicopters: one waiting on every helipad in the tiles loaded, the one
 * being flown, and the ones left where they landed.
 *
 * A pad's helicopter comes and goes with its tile. Once flown it is the
 * player's: it stays wherever it is left, and its pad stays empty — a
 * rebuilt tile does not put another there. Positions are kept in latitude
 * and longitude, like the player's and the cars', so an origin rebase does
 * not move them. The last few left behind are kept; older ones are let go.
 */

import type { Scene } from 'three';
import type { HelicopterModel } from './Helicopter.js';
import { HELI, animateHelicopterLights, createHelicopter, disposeHelicopter, orientHelicopter } from './Helicopter.js';
import type { HelipadRef } from './ThreeRenderer.js';
import type { Tank } from '../core/economy/energy.js';
import { foundTank } from '../core/economy/energy.js';

type ToLocal = (lat: number, lon: number) => { east: number; north: number };
type ToGeo = (east: number, north: number) => { lat: number; lon: number };

export interface FleetHeli {
  model: HelicopterModel;
  lat: number;
  lon: number;
  /** Height of what the skids stand on, and its heading. */
  y: number;
  yaw: number;
  /** The pad it is waiting on, until it is first flown. */
  pad?: string;
  /** Its Jet A-1. */
  tank: Tank;
}

/** Left-behind helicopters to keep before letting the oldest go. */
const KEEP = 3;
/** Half the airframe's footprint, for walking into it: across and along. */
const HALF_WIDTH = 1.3;
const HALF_LENGTH = 3.4;
/** How far behind the mast the airframe's middle is, metres. */
const MIDDLE_BACK = 1.1;
/** The cabin's roof over the skids, metres. */
const CABIN_TOP = HELI.skidHeight + 2.1;

export class HeliFleet {
  readonly helis: FleetHeli[] = [];
  /** The one being flown, if any. It is placed by the flying, not here. */
  flown: FleetHeli | undefined;
  /** Pads whose helicopter has been taken, never to be refilled. */
  private readonly emptied = new Set<string>();

  constructor(private readonly scene: Scene) {}

  /** A new helicopter standing at (lat, lon, y), facing `yaw`. */
  add(lat: number, lon: number, y: number, yaw: number, pad?: string): FleetHeli {
    const model = createHelicopter();
    model.root.matrixAutoUpdate = true;
    this.scene.add(model.root);
    const heli: FleetHeli = { model, lat, lon, y, yaw, pad, tank: foundTank('heli', Math.round(lat * 1e5) * 31 + Math.round(lon * 1e5)) };
    this.helis.push(heli);
    return heli;
  }

  /**
   * Keep a helicopter on every pad loaded that has not been emptied, and let
   * go of the waiting ones whose tiles have gone.
   */
  sync(pads: readonly HelipadRef[], toGeo: ToGeo): void {
    const present = new Map(pads.map(p => [p.id, p]));
    for (let i = this.helis.length - 1; i >= 0; i--) {
      const h = this.helis[i];
      if (h.pad === undefined) continue;
      const pad = present.get(h.pad);
      if (pad === undefined) {
        this.remove(i);
        continue;
      }
      // A rebuilt tile can move its pad: a roof whose real height arrived late.
      const geo = toGeo(pad.x, -pad.z);
      h.lat = geo.lat; h.lon = geo.lon; h.y = pad.y; h.yaw = pad.yaw;
    }
    const waiting = new Set(this.helis.map(h => h.pad));
    for (const pad of pads) {
      if (waiting.has(pad.id) || this.emptied.has(pad.id)) continue;
      const geo = toGeo(pad.x, -pad.z);
      this.add(geo.lat, geo.lon, pad.y, pad.yaw, pad.id);
    }
  }

  /** Make `h` the one being flown: its pad, if it had one, is empty from now on. */
  fly(h: FleetHeli): void {
    if (h.pad !== undefined) this.emptied.add(h.pad);
    h.pad = undefined;
    this.flown = h;
  }

  /** Leave the one being flown where it is, and let go of the oldest past `KEEP`. */
  leave(): void {
    this.flown = undefined;
    const left = this.helis.filter(h => h.pad === undefined);
    while (left.length > KEEP) this.remove(this.helis.indexOf(left.shift()!));
  }

  /** Stand the waiting and left-behind helicopters where they are, rotors still. */
  place(toLocal: ToLocal): void {
    for (const h of this.helis) {
      if (h === this.flown) continue;
      const at = toLocal(h.lat, h.lon);
      h.model.root.visible = true;
      h.model.root.position.set(at.east, h.y, -at.north);
      orientHelicopter(h.model.root, h.yaw, 0, 0);
      // Shut down: lamps dark, rotor still.
      animateHelicopterLights(h.model, false, 0, 0);
    }
  }

  /** Scene (x, z) of a helicopter's middle, a little behind its mast. */
  private middle(h: FleetHeli, toLocal: ToLocal): { x: number; z: number } {
    const at = toLocal(h.lat, h.lon);
    return { x: at.east + Math.sin(h.yaw) * MIDDLE_BACK, z: -at.north + Math.cos(h.yaw) * MIDDLE_BACK };
  }

  /** The nearest standing helicopter within `radius` of its side at scene (x, z). */
  nearest(x: number, z: number, radius: number, toLocal: ToLocal): FleetHeli | undefined {
    let best: FleetHeli | undefined;
    let bestD = radius;
    for (const h of this.helis) {
      if (h === this.flown) continue;
      const m = this.middle(h, toLocal);
      // Distance from the airframe's box rather than its middle: it is long.
      const c = Math.cos(h.yaw), s = Math.sin(h.yaw);
      const dx = x - m.x, dz = z - m.z;
      const u = Math.max(0, Math.abs(dx * c - dz * s) - HALF_WIDTH);
      const w = Math.max(0, Math.abs(dx * s + dz * c) - HALF_LENGTH);
      const d = Math.hypot(u, w);
      if (d < bestD) { bestD = d; best = h; }
    }
    return best;
  }

  /**
   * Push a round body at scene (x, z), spanning `feet`..`head`, out of any
   * standing helicopter's cabin. Undefined when clear.
   */
  collide(x: number, z: number, radius: number, feet: number, head: number, toLocal: ToLocal): { x: number; z: number } | undefined {
    let px = x, pz = z, hit = false;
    for (const h of this.helis) {
      if (h === this.flown) continue;
      if (head <= h.y || feet >= h.y + CABIN_TOP) continue;
      const m = this.middle(h, toLocal);
      const c = Math.cos(h.yaw), s = Math.sin(h.yaw);
      const dx = px - m.x, dz = pz - m.z;
      const u = dx * c - dz * s, w = dx * s + dz * c;
      const hu = HALF_WIDTH + radius, hw = HALF_LENGTH + radius;
      if (Math.abs(u) >= hu || Math.abs(w) >= hw) continue;
      let nu = u, nw = w;
      if (hu - Math.abs(u) < hw - Math.abs(w)) nu = Math.sign(u || 1) * hu;
      else nw = Math.sign(w || 1) * hw;
      px = m.x + nu * c + nw * s;
      pz = m.z - nu * s + nw * c;
      hit = true;
    }
    return hit ? { x: px, z: pz } : undefined;
  }

  private remove(i: number): void {
    const [gone] = this.helis.splice(i, 1);
    if (gone === this.flown) this.flown = undefined;
    this.scene.remove(gone.model.root);
    disposeHelicopter(gone.model);
  }
}
